import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadDropzone from './UploadDropzone';
import {
  isPdfFile,
  replaceUploadItemFile,
  rotatePdfFile90,
} from './uploadItemUtils';

/**
 * Layout tipo iLovePDF: grade de cards + sidebar de ação.
 * Usado por todas as ferramentas ARCA PDF.
 */
export default function ArcaPdfStudioLayout({
  title,
  info,
  limits,
  error,
  onClearError,
  onRotateError,
  success,
  onClearSuccess,
  items,
  setItems,
  loading = false,
  dropzone = {},
  enableRotate = true,
  enableSortAlpha = true,
  sidebarExtras = null,
  actionLabel,
  actionLoadingLabel,
  actionStartIcon = null,
  onAction,
  actionDisabled = false,
  showClear = true,
  onClear,
  clearLabel = 'Limpar lista',
  belowStudio = null,
  showBack = true,
  maxWidth = 1200,
}) {
  const navigate = useNavigate();
  const [rotatingId, setRotatingId] = useState(null);

  const handleRotate = useCallback(
    async (item) => {
      if (!enableRotate || !item?.id || !item.file || !isPdfFile(item.file)) return;
      setRotatingId(item.id);
      try {
        const rotated = await rotatePdfFile90(item.file);
        replaceUploadItemFile(item.id, rotated, setItems);
      } catch (e) {
        console.error('[ArcaPdfStudio] rotate', e);
        onRotateError?.(e.message || 'Não foi possível girar este PDF.');
      } finally {
        setRotatingId(null);
      }
    },
    [enableRotate, setItems, onRotateError]
  );

  const handleSortAlpha = useCallback(() => {
    setItems((prev) =>
      [...prev].sort((a, b) =>
        String(a.file?.name || '').localeCompare(String(b.file?.name || ''), 'pt-BR', {
          sensitivity: 'base',
        })
      )
    );
  }, [setItems]);

  const {
    accept = 'application/pdf,.pdf',
    multiple = true,
    maxSizeMb = 50,
    onFilesAdd,
    label = 'Arraste arquivos aqui ou clique para selecionar',
    hint,
    ...restDrop
  } = dropzone;

  return (
    <Box sx={{ maxWidth, mx: 'auto', width: '100%' }}>
      {showBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/arca/pdf')} sx={{ mb: 2 }}>
          Voltar às ferramentas
        </Button>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 300px' },
          gap: 0,
          minHeight: { md: 560 },
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ minHeight: 420, bgcolor: '#eceff1', position: 'relative' }}>
          <UploadDropzone
            variant="studio"
            accept={accept}
            multiple={multiple}
            maxSizeMb={maxSizeMb}
            disabled={loading || Boolean(rotatingId)}
            items={items}
            onFilesAdd={onFilesAdd}
            onReorder={setItems}
            onRemove={(id) => setItems((prev) => prev.filter((p) => p.id !== id))}
            onRotate={enableRotate ? handleRotate : undefined}
            rotatingId={rotatingId}
            onSortAlpha={enableSortAlpha && multiple ? handleSortAlpha : undefined}
            label={label}
            hint={hint}
            {...restDrop}
          />
        </Box>

        <Paper
          elevation={0}
          square
          sx={{
            p: 2.5,
            borderLeft: { md: '1px solid' },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            minHeight: { xs: 'auto', md: 560 },
          }}
        >
          <Typography variant="h5" fontWeight={800} textAlign="center" sx={{ mb: 2 }}>
            {title}
          </Typography>

          {info && (
            <Alert
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ mb: 2, alignItems: 'flex-start' }}
            >
              {info}
            </Alert>
          )}

          {limits && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {limits}
            </Typography>
          )}

          {sidebarExtras}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={onClearSuccess}>
              {success}
            </Alert>
          )}

          <Box sx={{ flex: 1 }} />

          <Stack spacing={1.25}>
            <Button
              variant="contained"
              color="error"
              size="large"
              fullWidth
              startIcon={!loading ? actionStartIcon : null}
              endIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </Box>
                )
              }
              disabled={loading || actionDisabled || Boolean(rotatingId)}
              onClick={onAction}
              sx={{ textTransform: 'none', fontWeight: 800, py: 1.4 }}
            >
              {loading ? actionLoadingLabel || 'Processando…' : actionLabel}
            </Button>
            {showClear && (
              <Button
                variant="text"
                disabled={loading || !items.length}
                onClick={() => (onClear ? onClear() : setItems([]))}
                sx={{ textTransform: 'none' }}
              >
                {clearLabel}
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>

      {belowStudio}
    </Box>
  );
}
